import React from "react";
import locale from "@bonliva-crm/locale";
import { useObserver } from "mobx-react";
import {
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Clear";
import { makeStyles } from "@material-ui/core/styles";
import { ThemeType } from "@theme";
import { Checkbox, Text } from "@components/formx";
import { Specialization, SSKStore } from "./SSKStore";
import { ExpKnownSystem, ExpMultiSelect, ExpTopPanel, OutlinedInput } from "../Common";
import { useConsultantEditorContext } from "../../ConsultantEditorContext";
import { ConsultantEditorStore } from "../../ConsultantEditorStore";
import { compact } from "lodash";
import { CUSTOM_GROUPING } from "@consultants/ConsultantEditor/components/SSK/customGrouping";
import { encodeGenericAOE } from "@bonliva-crm/common/ConsultantSearch";

type Props = {
  store: SSKStore;
};

type SpecializationProps = {
  label: string;
  value: string;
  parent?: string;
};

export function SSKExpertise(props: Props) {
  const { store } = props;

  const editorStore: ConsultantEditorStore = useConsultantEditorContext();
  const sskStore = store;
  const profItem = `professions.${sskStore.storeIndex}`;
  const classes = useStyles({});
  const specializationOptions: Array<SpecializationProps> =
    sskStore.selectSpecializationList.map(parseSpec);
  const groups = compact(specializationOptions.map((x) => x.parent))
    .unique()
    .toLabelValue();
  specializationOptions.push(...groups);
  const specs = [];

  groups.forEach(({ label }) => {
    const children = [];
    specializationOptions.forEach((s) => {
      if (s.parent === label) {
        children.push({
          label: s.label,
          value: encodeGenericAOE(label, s.label),
          parent: label
        });
      }
    });
    specs.push({
      label,
      value: label,
      children
    });
  });

  const uniqSpecs = specs.uniqueBy((x) => x.value + x.parent);

  const option = [...uniqSpecs, ...specializationOptions];

  const uniqSpecsWithGroup = option.uniqueBy((x) => x.value + x.parent);

  return useObserver(() => (
    <>
      <Paper className={classes.info}>
        <ExpTopPanel
          certifiedPath={`${profItem}.certifiedFrom`}
          certifiedFromTxt="Leg. SSK cert. from:"
          onDeleteClick={sskStore.deleteExpertise}
          withPrescriptionCode
        />

        <ExpMultiSelect
          name={locale.AddVUB}
          selectOptions={sskStore.selectAoEList}
          onChange={(v) => sskStore.setSelectedAoE(v)}
          selectedVal={sskStore.selectedAoE}
          btnClick={(_) => sskStore.addAoE()}
          btnDisabled={!sskStore.selectedAoE}
        />

        <ExpMultiSelect
          name={locale.AddExp}
          selectOptions={uniqSpecsWithGroup}
          onChange={(val) => sskStore.setSelectedSpecialization(val)}
          selectedVal={sskStore.selectedSpecialization}
          btnClick={(_) => sskStore.addSpec()}
          btnDisabled={!sskStore.selectedSpecialization}
          SelectProps={
            groups.length > 0
              ? { groupBy: "parent", hideGroupCheckbox: true }
              : {}
          }
        />
      </Paper>

      {sskStore.areasOfExpertise.length !== 0 && (
        <SSKTable
          name="VUB"
          yearFromTxt={locale.VUBYearFrom}
          defaultAOE={store.defaultAOE}
          profItem={`${profItem}.areasOfExpertise`}
          items={sskStore.areasOfExpertise}
          deleteRow={sskStore.removeSpec}
        />
      )}

      {sskStore.specializations.length !== 0 && (
        <SSKTable
          name={locale.Specialization}
          yearFromTxt={locale.ExperienceYearFrom}
          profItem={`${profItem}.specializations`}
          defaultAOE={store.defaultAOE}
          items={sskStore.specializations}
          deleteRow={sskStore.removeSpec}
        />
      )}

      <Paper style={{ padding: 16 }}>
        <Grid container spacing={1}>
          <Grid item xs={12} md={6}>
            <ExpKnownSystem
              options={sskStore.systems}
              onChange={sskStore.addSystems}
              value={sskStore.selectedSystems}
              hasValidated={true}
              validate={(e, c) => {
                if (c) {
                  editorStore.consultantCVManager.approveKey(
                    `professions.${sskStore.storeIndex}.systems`
                  );
                }
              }}
            />
          </Grid>
        </Grid>
      </Paper>
    </>
  ));
}

type SpecializationProp = {
  name: string;
  profItem: string;
  defaultAOE: string;
  deleteRow: (path: string, i: number) => void;
  yearFromTxt: string;
  items: Specialization[];
};

function itemsInGroup(group: string, items: any) {
  return items.filter((x) => x.parent === group);
}

function SSKTable(props: SpecializationProp) {
  const { items, profItem, name, deleteRow, yearFromTxt, defaultAOE } = props;
  const itemNames = [];
  const groups = [];

  for (const [index, { name }] of items.entries()) {
    const result = parseSpec(name, index);
    itemNames.push(result);
    groups.push(result.parent);
  }

  const sortedGroups = groups.sortBy((x) => x).unique();

  return useObserver(() => (
    <Paper>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell align="left" style={{ width: "15%" }}>
              {name}
            </TableCell>
            <TableCell align="center" style={{ width: 64 }}>
              {locale.Experienced}
            </TableCell>
            <TableCell align="center" style={{ width: "10rem" }}>
              {yearFromTxt}
            </TableCell>
            <TableCell align="center" style={{ width: 64, whiteSpace: "nowrap" }}>
              {locale.Interested}
            </TableCell>
            <TableCell align="center">{locale.Comment}</TableCell>
            <TableCell style={{ width: 48 }} />
          </TableRow>
        </TableHead>

        <TableBody>
          {sortedGroups.map((group) => (
            <React.Fragment key={group}>
              {group && group !== defaultAOE && (
                <TableRow style={{ background: "aliceblue" }}>
                  <TableCell colSpan={5} style={{ fontSize: "1.1rem", fontWeight: 500 }}>
                    {group}
                  </TableCell>
                </TableRow>
              )}

              {itemsInGroup(group, itemNames).map((item) => {
                const itemName = `${profItem}.${item.index}`;

                return (
                  <TableRow key={itemName}>
                    <TableCell style={{ fontWeight: "bold" }}>{item.label}</TableCell>

                    <TableCell align="center">
                      <Checkbox name={`${itemName}.qualified`} />
                    </TableCell>

                    <TableCell align="center">
                      <OutlinedInput
                        name={`${itemName}.yearFrom`}
                        label={locale.ExperienceYearFrom}
                      />
                    </TableCell>

                    <TableCell align="center">
                      <Checkbox name={`${itemName}.interested`} />
                    </TableCell>

                    <TableCell align="center">
                      <Text name={`${itemName}.comment`} label={locale.WriteComment} />
                    </TableCell>

                    <TableCell align="center">
                      <IconButton
                        onClick={() => deleteRow(profItem, item.index)}
                        disabled={item.value === defaultAOE}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </Paper>
  ));
}

function parseSpec(specialization: string, index?: number) {
  if (!specialization.includes("(") || specialization === "Ögonkirurgi (ej katarakt)") {
    const obj: { label: string; value: string; parent?: string; index?: number } = {
      label: specialization,
      value: specialization,
      index
    };
    const [filter] = CUSTOM_GROUPING.filter((v) => v.value === specialization);

    if (filter) {
      obj.parent = filter.parent;
    }

    return obj;
  }

  const [name, group] = specialization.slice(0, specialization.length - 1).split(" (");

  return { label: name, value: specialization, parent: group, index };
}

const useStyles = makeStyles((theme: ThemeType) => ({
  info: {
    ...theme.mixins.childrenMarginBottom(8),
    padding: 16,
    position: "relative"
  }
}));
